import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { PendingAdd } from '../lib/userLimits'
import { isPlausibleEmail, normalizeEmail } from '../lib/email'
import { Button } from './ui/Button'
import { QtyStepper } from './ui/QtyStepper'

type Mode = 'default' | 'custom'

/** The prototype's `add-limit-user-modal` (`openAddUser`,
 * subscription-portal.html): an email, then the limit the user starts on —
 * the first radio reads "Default limit" while a default is enforced and "No
 * limit" otherwise (`addLimitLabel`, read from the page's draft), the second
 * is "Custom limit" with its stepper inline. Touching the figure selects
 * Custom. Cancel or the backdrop closes it; Enter in the email submits.
 *
 * Add only drafts the user, as the prototype's `add-limit-user-save` does:
 * the email is checked here — required, plausible, and not already on the
 * list or drafted — and the user joins the list as a pending change, written
 * by the page Save (`POST /api/v1/end-users`) and dropped by its Discard.
 * Available whether or not automatic registration is on
 * (ARCHITECTURE_DECISIONS.md §53). */
export function AddUserModal({
  defaultEnforced,
  existingEmails,
  onAdd,
  onClose,
}: Readonly<{
  /** The default limit is switched on in the page's draft. */
  defaultEnforced: boolean
  /** The emails on this product's list as drafted, saved and pending alike. */
  existingEmails: readonly string[]
  /** The user to draft; the modal closes after. */
  onAdd: (add: PendingAdd) => void
  onClose: () => void
}>) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [mode, setMode] = useState<Mode>('default')
  const [custom, setCustom] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  function submit(event: FormEvent) {
    event.preventDefault()
    // Normalised and checked as the API does it (lib/email.ts).
    const value = normalizeEmail(email)
    if (!value) {
      setError(t('userLimits.addUserModal.emailRequired'))
      return
    }
    if (!isPlausibleEmail(value)) {
      setError(t('userLimits.addUserModal.emailInvalid'))
      return
    }
    if (existingEmails.some((e) => e.toLowerCase() === value)) {
      setError(t('userLimits.addUserModal.emailExists', { email: value, count: 1 }))
      return
    }
    onAdd({ email: value, limit: mode === 'custom' ? custom : null })
    onClose()
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000]">
      {/* The backdrop is a sibling of the dialog (AdminConfirmModal's
          pattern), a pointer convenience hidden from assistive tech — Cancel
          is the keyboard path. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="add-user-backdrop"
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onClose}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby="add-user-title"
        className="relative m-0 p-0 border-0 text-inherit bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-lg)] w-[420px] max-w-[90vw] overflow-hidden"
      >
        <form onSubmit={submit} noValidate>
          <div className="px-6 pt-5 pb-4 border-b border-[var(--border-light)]">
            <h3 id="add-user-title" className="text-[16px] font-semibold text-[var(--text-primary)]">
              {t('userLimits.addUserModal.title')}
            </h3>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{t('userLimits.addUserModal.subtitle')}</p>
          </div>
          <div className="px-6 py-5">
            <div className="mb-[22px]">
              <label htmlFor="add-user-email" className="block font-semibold text-[13px] mb-2 text-[var(--text-primary)]">
                {t('userLimits.addUserModal.emailLabel')}
              </label>
              <input
                ref={inputRef}
                id="add-user-email"
                type="email"
                value={email}
                placeholder={t('userLimits.addUserModal.emailPlaceholder')}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'add-user-error' : undefined}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-md text-[13px] text-[var(--text-primary)] bg-[var(--bg-input)] transition-[border-color,box-shadow] focus:outline-none focus:border-[var(--border-focus)] focus:shadow-[0_0_0_3px_var(--accent-subtle)]"
              />
              {error && (
                <div id="add-user-error" role="alert" className="mt-[7px] text-[12px] text-[var(--danger)]">
                  {error}
                </div>
              )}
            </div>
            <fieldset>
              <legend className="block font-semibold text-[13px] mb-2 text-[var(--text-primary)]">
                {t('userLimits.addUserModal.limitLabel')}
              </legend>
              <div className="flex flex-col gap-[7px]">
                <label className="flex items-center gap-[9px] text-[13px] text-[var(--text-primary)] cursor-pointer">
                  <input
                    type="radio"
                    name="add-user-limit"
                    checked={mode === 'default'}
                    onChange={() => setMode('default')}
                    className="w-[15px] h-[15px] m-0 cursor-pointer accent-[var(--accent)]"
                  />
                  <span>
                    {t(defaultEnforced ? 'userLimits.addUserModal.inheritDefault' : 'userLimits.addUserModal.inheritNone')}
                  </span>
                </label>
                <div className="flex items-center gap-[9px]">
                  <label className="flex items-center gap-[9px] text-[13px] text-[var(--text-primary)] cursor-pointer">
                    <input
                      type="radio"
                      name="add-user-limit"
                      checked={mode === 'custom'}
                      onChange={() => setMode('custom')}
                      className="w-[15px] h-[15px] m-0 cursor-pointer accent-[var(--accent)]"
                    />
                    <span>{t('userLimits.addUserModal.custom')}</span>
                  </label>
                  {/* `.radio-row-ctrl`: dimmed until Custom is chosen. It sits
                      inside the radio's label in the prototype and, while
                      dimmed, takes no pointer events (`.off`), so a click on
                      it only chooses Custom. Here the stepper ignores the
                      pointer while dimmed and a transparent native button
                      laid over it takes the click instead, choosing Custom
                      without stepping the figure. It is out of the tab order
                      and hidden from assistive tech: the Custom radio is the
                      keyboard path. Any change to the figure (a step or a
                      typed digit) also chooses Custom. */}
                  <span
                    data-testid="add-user-custom-ctrl"
                    className={`relative ml-auto transition-opacity ${mode === 'custom' ? '' : 'opacity-40'}`}
                  >
                    <span
                      data-testid="add-user-custom-stepper"
                      className={`inline-flex ${mode === 'custom' ? '' : 'pointer-events-none'}`}
                    >
                      <QtyStepper
                        value={custom}
                        min={0}
                        editable
                        label={t('userLimits.addUserModal.customValue')}
                        onChange={(next) => {
                          setCustom(next)
                          setMode('custom')
                        }}
                      />
                    </span>
                    {mode !== 'custom' && (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        data-testid="add-user-custom-choose"
                        onClick={() => setMode('custom')}
                        className="absolute inset-0 m-0 p-0 border-0 bg-transparent cursor-pointer"
                      />
                    )}
                  </span>
                </div>
              </div>
            </fieldset>
          </div>
          <div className="px-6 py-4 border-t border-[var(--border-light)] flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary">
              {t('userLimits.addUserModal.submit')}
            </Button>
          </div>
        </form>
      </dialog>
    </div>
  )
}
